/**
 * Presets row for the Options Tape: curated presets, the user's own, and save / rename /
 * delete / export / import. Applying a preset is the parent's job; this only renders choices.
 */

import React, { useRef, useState } from 'react';
import {
  CURATED_PRESETS,
  describeSettings,
  exportPresetsJson,
  parseImportedPresets,
  TapePreset,
} from './tapePresets';

export interface PresetsRowProps {
  /** Preset whose settings are on screen right now, or null when the user has edited away. */
  activeId: string | null;
  /** Preset the current settings were derived from, for the "Custom" line. */
  baseName: string | null;
  userPresets: TapePreset[];
  onApply: (p: TapePreset) => void;
  onSave: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onImport: (list: TapePreset[]) => void;
}

const PresetsRow: React.FC<PresetsRowProps> = ({
  activeId,
  baseName,
  userPresets,
  onApply,
  onSave,
  onRename,
  onDelete,
  onImport,
}) => {
  const [naming, setNaming] = useState<null | { mode: 'save' } | { mode: 'rename'; id: string }>(null);
  const [nameInput, setNameInput] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const all = [...CURATED_PRESETS, ...userPresets];
  const active = all.find((p) => p.id === activeId) ?? null;
  const userActive = !!active && userPresets.some((p) => p.id === active.id);

  const commitName = () => {
    const name = nameInput.trim();
    if (!name || !naming) return;
    if (naming.mode === 'save') onSave(name);
    else onRename(naming.id, name);
    setNaming(null);
    setNameInput('');
  };

  const doExport = () => {
    const blob = new Blob([exportPresetsJson(userPresets)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'options-tape-presets.json';
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const doImport = async (file: File | undefined) => {
    if (!file) return;
    const list = parseImportedPresets(await file.text());
    setImportMsg(list.length ? `Imported ${list.length}` : 'No presets in that file');
    if (list.length) onImport(list);
    window.setTimeout(() => setImportMsg(''), 4000);
  };

  return (
    <div className="ftpresets">
      <div className="ftprow">
        <span className="lab">Presets</span>
        <div className="ftpchips">
          {CURATED_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`chip${activeId === p.id ? ' on' : ''}`}
              title={p.description}
              onClick={() => onApply(p)}
            >
              {p.name}
              {p.settings.live && <span className="ftplive" aria-label="live">●</span>}
            </button>
          ))}
          <span className={`chip ftpcustom${activeId ? '' : ' on'}`} aria-current={activeId ? undefined : 'true'}>
            Custom
          </span>
        </div>
      </div>
      <div className="ftprow">
        <span className="lab">My presets</span>
        <div className="ftpchips">
          {userPresets.length === 0 && <span className="ftpempty">None yet — set filters, then save them.</span>}
          {userPresets.map((p) => (
            <span key={p.id} className={`ftpuser${activeId === p.id ? ' on' : ''}`}>
              <button type="button" className="ftpname" title={describeSettings(p.settings)} onClick={() => onApply(p)}>
                {p.name}
              </button>
              <button
                type="button"
                className="ftpicon"
                title="Rename"
                aria-label={`Rename ${p.name}`}
                onClick={() => { setNaming({ mode: 'rename', id: p.id }); setNameInput(p.name); }}
              >
                ✎
              </button>
              <button
                type="button"
                className="ftpicon del"
                title="Delete"
                aria-label={`Delete ${p.name}`}
                onClick={() => { if (window.confirm(`Delete preset "${p.name}"?`)) onDelete(p.id); }}
              >
                ×
              </button>
            </span>
          ))}
          {naming ? (
            <span className="ftpnaming">
              <input
                className="num"
                autoFocus
                maxLength={60}
                placeholder="Preset name…"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitName();
                  if (e.key === 'Escape') setNaming(null);
                }}
              />
              <button type="button" className="chip on" onClick={commitName} disabled={!nameInput.trim()}>
                {naming.mode === 'save' ? 'Save' : 'Rename'}
              </button>
              <button type="button" className="chip" onClick={() => setNaming(null)}>
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="chip"
              onClick={() => { setNaming({ mode: 'save' }); setNameInput(''); }}
              title="Save the current filters, rules, sort, view and live flag"
            >
              + Save current
            </button>
          )}
          <span className="ftpio">
            <button type="button" className="chip" disabled={!userPresets.length} onClick={doExport} title="Download your presets as JSON">
              Export
            </button>
            <button type="button" className="chip" onClick={() => fileRef.current?.click()} title="Add presets from a JSON file">
              Import
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => { doImport(e.target.files?.[0]); e.target.value = ''; }}
            />
            {importMsg && <span className="ftpempty">{importMsg}</span>}
          </span>
        </div>
      </div>
      <div className="ftpdesc">
        {active ? (
          <>
            <b>{active.name}</b> — {userActive ? describeSettings(active.settings) : active.description}
          </>
        ) : (
          <>
            <b>Custom</b> — {baseName ? `edited from ${baseName}.` : 'your own filters.'} Save it under My presets to keep it.
          </>
        )}
      </div>
    </div>
  );
};

export default PresetsRow;
